import {Card, Form, InputGroup, Table} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {INBOX_MESSAGES as M} from '../inboxMessages'
import {useLazyQuery} from '@apollo/client'
import {useEffect} from 'react'
import {toast} from 'react-toastify'
import {GET_EMAILS} from '../../../gql/queries/emailsQuery'
import {LoadingMsgs} from './LoadingMsgs'
import Lottie from 'lottie-react'
import lottieEmptyMsg from '../../../assets/lotties/empty-msg.json'

export interface GmailEmail {
  id: string
  snippet: string
  fromEmail: string
  subject: string
  date: string
}

interface ILoadEmailsResult {
  loadEmails: {
    emails: GmailEmail[]
    nextPageToken: string | null
  }
}

function parseSenderName(fromEmail: string): string {
  const match = fromEmail.match(/^(.+?)\s*</)
  return match ? match[1].trim() : fromEmail
}

function senderInitial(fromEmail: string): string {
  return parseSenderName(fromEmail).charAt(0).toUpperCase() || '?'
}

interface IInitInboxProps {
  ButtonMenu: JSX.Element
  updateInfo: (info: GmailEmail) => void
}

export const InitInboxContent: React.FC<IInitInboxProps> = ({ButtonMenu, updateInfo}) => {
  const intl = useIntl()
  const [gqlGetEmails, {data, loading, error}] = useLazyQuery<ILoadEmailsResult>(GET_EMAILS)

  useEffect(() => {
    gqlGetEmails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (error)
      toast.error(intl.formatMessage(M.errorWithMessage, {message: error.message}))
  }, [error])

  if (loading || !data) {
    return <LoadingMsgs />
  }

  const emails = data.loadEmails?.emails ?? []

  return (
    <>
      <Card.Header>
        <div className='d-flex align-items-center justify-content-between w-100'>
          <div>
            {emails.length > 0 && (
              <div className='form-check form-check-sm form-check-custom form-check-solid'>
                <input className='form-check-input widget-9-check' type='checkbox' value='1' />
              </div>
            )}
          </div>
          <div className='d-flex gap-2 align-items center'>
            <InputGroup>
              <InputGroup.Text id='basic-addon1'>
                <i className='bi bi-search' />
              </InputGroup.Text>
              <Form.Control
                placeholder={intl.formatMessage(M.searchPlaceholder)}
                aria-label={intl.formatMessage(M.search)}
                aria-describedby='basic-addon1'
              />
            </InputGroup>
            {ButtonMenu}
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        {emails.length > 0 ? (
          <div className='table-responsive'>
            <Table className='w-100 table-hover table-row-dashed fs-6 gy-5 my-0 dataTable'>
              <tbody>
                {emails.map((email) => (
                  <tr key={email.id}>
                    <td>
                      <div className='form-check form-check-sm form-check-custom form-check-solid'>
                        <input
                          className='form-check-input widget-9-check'
                          type='checkbox'
                          value='1'
                        />
                      </div>
                    </td>
                    <td>
                      <div className='d-flex gap-2'>
                        <button style={{backgroundColor: 'transparent', border: 0}}>
                          <i className='bi bi-star-fill fs-3' />
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className='d-flex align-items-center'>
                        <div className='symbol symbol-45px me-5'>
                          <div
                            className='symbol-label bg-primary text-white fw-bold fs-4'
                            style={{width: 45, height: 45, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                          >
                            {senderInitial(email.fromEmail)}
                          </div>
                        </div>
                        <div className='d-flex justify-content-start flex-column'>
                          <span className='text-dark fw-bold text-hover-primary fs-6'>
                            {parseSenderName(email.fromEmail)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <button
                        style={{border: 0, backgroundColor: 'transparent', textAlign: 'left'}}
                        className='text-dark fw-bold text-hover-primary d-block fs-6'
                        onClick={() => updateInfo(email)}
                      >
                        {email.subject || intl.formatMessage(M.noSubject)}
                      </button>
                      <span className='text-muted fs-7'>{email.snippet}</span>
                    </td>
                    <td className='text-end'>
                      <span className='text-muted me-2 fs-7 fw-semibold'>{email.date}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className='d-flex flex-center h-100 flex-column'>
            <h3>{intl.formatMessage(M.empty)}</h3>
            <div style={{maxWidth: '300px'}}>
              <Lottie animationData={lottieEmptyMsg} loop autoPlay />
            </div>
          </div>
        )}
      </Card.Body>
    </>
  )
}
